import styled from "@emotion/styled";
import type {
  Bundle,
  BundleEntry,
  DiagnosticReport,
  Observation
} from "fhir/r4";
import type { FC } from "react";
import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeftIcon, IconInfoCircle } from "src/constants/icons";
import { APP_CONTENT_WIDTH } from "src/constants/layout";
import { dateLocalString } from "src/lib/date";
import LoadingCubit, { LoadingKey } from "src/state/LoadingCubit/LoadingCubit";
import type { ObservationBundleSearchParams } from "src/state/ObservationBundleBloc/ObservationBundleBloc";
import ObservationBundleBloc from "src/state/ObservationBundleBloc/ObservationBundleBloc";
import { TrackEvent } from "src/state/Track/TrackCubit";
import { tracker } from "src/state/state";
import goldenHourConfidence from "src/ui/assets/patterns/goldenHourConfidence.jpg";
import goldenHourRefresh from "src/ui/assets/patterns/goldenHourRefresh.jpg";
import goldenHourSupport from "src/ui/assets/patterns/goldenHourSupport.jpg";
import midMorningConfidence from "src/ui/assets/patterns/midMorningConfidence.jpg";
import midMorningRefresh from "src/ui/assets/patterns/midMorningRefresh.jpg";
import midMorningSupport from "src/ui/assets/patterns/midMorningSupport.jpg";
import { LABS_BASE_PAGE_URL } from "src/ui/components/LabResultsDataSubPageRouter/LabResultsDataSubPageRouter";
import CollectionTitle from "src/ui/components/StyledComponents/CollectionTitle";
import Translate from "src/ui/components/Translate/Translate";
import LabResultRow from "src/ui/components/UserLabResults/LabResultRow";
import { ObservationBundleCategory } from "src/types/observationLaboratory";
import Loader from "../Loader/Loader";
import { useBlocNext } from "@blac/react";

//#region Styled components
const LabResultsWrapper = styled.div`
  background-color: #fff;
  border-radius: 0.5rem;
  box-shadow: var(--light-shadow);
  overflow: hidden;
`;

const LabResultsHero = styled.div<{
  backgroundImage: string | null | undefined;
}>`
  padding: 1.5rem;
  padding-top: calc(1.5rem + var(--safe-area-inset-top, 0px));
  border-radius: 0;
  margin: -1rem -1.6rem 1.5rem;
  overflow: hidden;
  position: relative;
  background-size: cover;
  background-position: bottom;
  background-repeat: no-repeat;
  ${({ backgroundImage }): string =>
    backgroundImage ? `background-image: url(${backgroundImage});` : ""}

  @media screen and (min-width: ${APP_CONTENT_WIDTH}px) {
    padding: 2rem;
    border-radius: 0.5rem;
    margin: 0 0 1.5rem;
  }
`;

const LabResultsTitle = styled.h5`
  margin-bottom: 1rem !important;

  @media screen and (min-width: 768px) {
    margin-bottom: 1.5rem !important;
  }
`;

const DatesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const DateRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  > small {
    min-width: 4.5rem;
  }
`;

const DateValue = styled.span`
  border-radius: 0.125rem;
  background: var(--Secondary-Dark-Cream, #f2efe7);
  padding: 0.125rem;
  font-family:
    "Monaco",
    "STK Bureau",
    "Open Sans",
    -apple-system,
    Roboto,
    Arial,
    sans-serif;
  font-size: 0.75rem;
`;

const IconWrapperButton = styled(Link)`
  background-color: var(--back-button-bg, rgba(33, 33, 33, 0.06));
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  width: fit-content;
  padding: 0.125rem;
  margin-bottom: 1.5rem;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const HieInfoBanner = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  margin-bottom: 1.5rem;
  border-radius: 0.5rem;
  background: rgba(95, 31, 143, 0.12);
  border: 1px solid rgba(95, 31, 143, 0.24);
  color: var(--Accessible-PurpleHaze, #5f1f8f);
  font-size: 0.875rem;

  svg {
    flex-shrink: 0;
    width: 1.25rem;
    height: 1.25rem;
  }
`;
//#endregion

// Background image
const backgroundImagesMap = new Map<string | null | undefined, string>([
  ["midMorningSupport", midMorningSupport],
  ["midMorningRefresh", midMorningRefresh],
  ["midMorningConfidence", midMorningConfidence],
  ["goldenHourSupport", goldenHourSupport],
  ["goldenHourRefresh", goldenHourRefresh],
  ["goldenHourConfidence", goldenHourConfidence]
]);

// Helper for date rendering
const renderDate = (date?: string): string => {
  return dateLocalString(date, { dateStyle: "medium" });
};

const ListLabsFromBundle: FC = () => {
  const [searchParams] = useSearchParams();
  const searchParamsObj: ObservationBundleSearchParams = Object.fromEntries([
    ...searchParams
  ]);
  const bundleInstanceId = `bundle:${searchParamsObj.observationDate ?? ""}:${searchParamsObj.category ?? ""}:${searchParamsObj.source ?? ""}:${searchParamsObj.labOrderId ?? ""}`;

  const [
    { observationBundle, observations },
    {
      loadObservationBundle,
      getObservationBundleLabel,
      getObservationsFromBundle,
      getObservationBundleCategory,
      getObservationBundleSourceName
    }
  ] = useBlocNext(ObservationBundleBloc, { id: bundleInstanceId });

  const [loadingKeys] = useBlocNext(LoadingCubit);

  const isLoading = loadingKeys.includes(LoadingKey.loadingObservationBundle);

  useEffect(() => {
    void loadObservationBundle(searchParamsObj);

    tracker.track(TrackEvent.LabReportOpened, {
      data: {
        Date: searchParamsObj.observationDate,
        Category: searchParamsObj.category,
        Source: searchParamsObj.source,
        "Lab Order ID": searchParamsObj.labOrderId
      }
    });
  }, []);

  useEffect(() => {
    getObservationsFromBundle(observationBundle);
  }, [observationBundle]);

  const diagnosticReport = useMemo(() => {
    return observationBundle?.entry?.find(
      (entry) => entry.resource?.resourceType === "DiagnosticReport"
    ) as BundleEntry<DiagnosticReport> | undefined;
  }, [observationBundle]);

  const observationBundleTitle = useMemo(() => {
    if (!observationBundle) {
      return "";
    }

    return (
      getObservationBundleLabel(
        observationBundle as unknown as Bundle<Observation>
      ) ?? ""
    );
  }, [observationBundle]);

  const collectedAtDate = useMemo(() => {
    return renderDate(diagnosticReport?.resource?.effectiveDateTime);
  }, [diagnosticReport]);

  const backgroundImage = useMemo(() => {
    if (!observationBundle) {
      return undefined;
    }

    const randomImageIdentifier =
      new Date(observationBundle.timestamp ?? "").getDate() +
      new Date(observationBundle.timestamp ?? "").getHours();
    const backgroundImagesMapKeys = [...backgroundImagesMap.keys()];
    const index = randomImageIdentifier % (backgroundImagesMapKeys.length - 1);
    const randomImageKey = backgroundImagesMapKeys[index];

    return backgroundImagesMap.get(randomImageKey);
  }, [observationBundle]);

  const category = useMemo(
    () =>
      observationBundle
        ? getObservationBundleCategory(
            observationBundle as unknown as Bundle<
              DiagnosticReport | Observation
            >
          )
        : undefined,
    [observationBundle]
  );
  const isImported = category === ObservationBundleCategory.IMPORTED;

  const sourceName = useMemo(
    () =>
      observationBundle
        ? getObservationBundleSourceName(
            observationBundle as unknown as Bundle<
              DiagnosticReport | Observation
            >
          )
        : undefined,
    [observationBundle]
  );

  if (isLoading) {
    return <Loader fullPage active />;
  }

  return (
    <>
      <LabResultsHero
        backgroundImage={backgroundImage}
        className="lab-results-hero"
      >
        <div>
          <IconWrapperButton to={LABS_BASE_PAGE_URL}>
            <ArrowLeftIcon />
          </IconWrapperButton>

          <LabResultsTitle className="m0 strong">
            {observationBundleTitle}
          </LabResultsTitle>

          <DatesWrapper>
            <DateRow>
              <small className="m0">
                <Translate msg="collected" />
              </small>
              <DateValue>
                {collectedAtDate || (
                  <Translate msg="labResults.detail.dateNotAvailable" />
                )}
              </DateValue>
            </DateRow>
            <DateRow>
              <small className="m0">
                <Translate msg="source" />
              </small>
              <DateValue>
                {sourceName ?? (
                  <Translate msg="labResults.detail.notProvided" />
                )}
              </DateValue>
            </DateRow>
          </DatesWrapper>
        </div>
      </LabResultsHero>

      {isImported && (
        <HieInfoBanner>
          <IconInfoCircle />
          <span>
            <Translate msg="labResults.detail.hieBanner" />
          </span>
        </HieInfoBanner>
      )}

      <CollectionTitle style={{ marginBottom: "0.6rem" }}>
        <Translate msg="results" />
      </CollectionTitle>

      <LabResultsWrapper>
        {(observations ?? [])
          .filter(Boolean)
          .map(
            (obs) =>
              obs.resource && (
                <LabResultRow
                  key={obs.resource.id}
                  observation={obs.resource}
                />
              )
          )}
      </LabResultsWrapper>
    </>
  );
};

export default ListLabsFromBundle;
